// 세로 회전 안내띠(first30-mobile): 폰을 세로로 들면 게임이 90도 돌아 가로 화면으로 보이지만 작다.
// 인트로가 끝난 뒤 처음 세로일 때만 화면 위쪽 가장자리에 작은 띠("휴대폰을 가로로 돌리면 더 크게 즐길 수 있어요")를 4초 보여 준다.
// #app 밖(body 직속)에 두어 회전되지 않은 채 바로 읽힌다. 탭하면 닫힘, 세션당 1번(sessionStorage), 가로가 되면 즉시 숨김.
// 데스크톱(html.dh-land 없음)이나 이미 본 세션에서는 리스너도 붙이지 않고, 판정은 폴링 없이 방향·크기 변화 이벤트와 인트로 변화 관찰로만 한다.
export const ROTATE_HINT_KEY = 'dungeonhero.ui.rotateHint';
export const ROTATE_HINT_MS = 4000;
export const ROTATE_HINT_TEXT = '휴대폰을 가로로 돌리면 더 크게 즐길 수 있어요';

export interface RotateHintState { rotatedMode: boolean; portrait: boolean; introUp: boolean; shownBefore: boolean }
/** 지금 띠를 띄워도 되는가(순수 함수) */
export function rotateHintShouldShow(s: RotateHintState): boolean {
  return s.rotatedMode && s.portrait && !s.introUp && !s.shownBefore;
}

function seenBefore(): boolean { try { return sessionStorage.getItem(ROTATE_HINT_KEY) === '1'; } catch { return false; } }
function markSeen(): void { try { sessionStorage.setItem(ROTATE_HINT_KEY, '1'); } catch { /* 저장 못 해도 이번 실행에서는 한 번만 */ } }

const ICON = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" style="flex:0 0 20px"><path fill="none" stroke="#ffd76a" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M7 3.5h6a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 13 14.5H7A1.5 1.5 0 0 1 5.5 13V5A1.5 1.5 0 0 1 7 3.5zM11 20.5a7 7 0 0 0 7-7M18 13.5l-1.8 2M18 13.5l2 1.7"/></svg>';

/** 폰이 아니거나(dh-land 없음) 이번 세션에 이미 봤다면 영영 못 띄우므로 리스너를 붙이지 않는다(순수 함수) */
export function rotateHintCanEver(rotatedMode: boolean, shownBefore: boolean): boolean {
  return rotatedMode && !shownBefore;
}

export class RotateHint {
  private box: HTMLDivElement | null = null;
  private timer = 0;
  private done = seenBefore();
  private readonly mq: MediaQueryList | null;
  private attached = false;
  private introObs: MutationObserver | null = null;
  private readonly onChange = (): void => {
    if (this.box && !this.mq?.matches) this.hide();
    if (!this.done && this.shouldShow()) this.show();
    // 띠가 떠 있는 동안엔 가로로 돌리면 바로 숨겨야 하므로 리스너를 남기고, 숨긴 뒤(hide)에 뗀다
    if (this.done && !this.box) this.detach();
  };

  constructor() {
    this.mq = typeof matchMedia === 'function' ? matchMedia('(orientation: portrait)') : null;
    if (!rotateHintCanEver(document.documentElement.classList.contains('dh-land'), this.done)) return;
    // 폴링 없이 방향·크기 변화 이벤트로만 판정한다(인트로가 끝나는 때도 화면 크기·방향 이벤트나 인트로 숨김 뒤 다음 변화에서 잡힌다).
    this.attached = true;
    this.mq?.addEventListener?.('change', this.onChange);
    window.addEventListener('orientationchange', this.onChange);
    window.addEventListener('resize', this.onChange);
    window.visualViewport?.addEventListener('resize', this.onChange);
    // 인트로가 닫히는 순간(클래스·스타일 변화)도 이벤트로 받는다(폴링 대신)
    const intro = document.getElementById('intro');
    if (intro && typeof MutationObserver === 'function') {
      this.introObs = new MutationObserver(this.onChange);
      this.introObs.observe(intro, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    }
    this.onChange();
  }

  /** 리스너를 모두 뗀다(이미 보여 줬거나 정리할 때) */
  dispose(): void {
    this.detach();
    this.hide();
  }

  private detach(): void {
    if (!this.attached) return;
    this.attached = false;
    this.mq?.removeEventListener?.('change', this.onChange);
    window.removeEventListener('orientationchange', this.onChange);
    window.removeEventListener('resize', this.onChange);
    window.visualViewport?.removeEventListener('resize', this.onChange);
    this.introObs?.disconnect();
    this.introObs = null;
  }

  private shouldShow(): boolean {
    const intro = document.getElementById('intro');
    const introUp = !!intro && !intro.classList.contains('off') && getComputedStyle(intro).display !== 'none';
    return rotateHintShouldShow({
      rotatedMode: document.documentElement.classList.contains('dh-land'),
      portrait: !!this.mq?.matches, introUp, shownBefore: this.done,
    });
  }

  private show(): void {
    this.done = true;
    markSeen();
    const b = document.createElement('div');
    b.dataset.testid = 'rotate-hint';
    b.setAttribute('role', 'status');
    b.innerHTML = ICON + '<span style="min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + ROTATE_HINT_TEXT + '</span>';
    b.style.cssText = 'position:fixed;z-index:2000000000;left:50%;top:calc(env(safe-area-inset-top,0px) + 4px);transform:translateX(-50%);box-sizing:border-box;' +
      'max-width:calc(100vw - 16px);height:32px;padding:0 12px;display:flex;align-items:center;gap:7px;border-radius:16px;' +
      'background:rgba(16,22,42,.94);border:1.5px solid #c8963e;box-shadow:0 3px 10px rgba(0,0,0,.55);color:#fff2c2;' +
      'font:700 12.5px system-ui,"Malgun Gothic","Apple SD Gothic Neo",sans-serif;pointer-events:auto;touch-action:manipulation;cursor:pointer;';
    b.addEventListener('pointerdown', (e) => { e.preventDefault(); e.stopPropagation(); });
    b.addEventListener('click', () => this.hide());
    document.body.appendChild(b);
    this.box = b;
    this.timer = window.setTimeout(() => this.hide(), ROTATE_HINT_MS);
  }

  hide(): void {
    window.clearTimeout(this.timer);
    this.box?.remove();
    this.box = null;
    if (this.done) this.detach();
  }
}
